import type {
  ArquivoInfo,
  Cliente,
  Item,
  ItemStatus,
  Pedido,
  PedidoStatus,
  Produto,
} from '../../types/orders';

function areArquivosShallowEqual(
  first?: ArquivoInfo | null,
  second?: ArquivoInfo | null,
): boolean {
  if (!first && !second) return true;
  if (!first || !second) return false;

  return (
    first.id === second.id &&
    first.nome === second.nome &&
    first.diretorio === second.diretorio &&
    first.sys_save === second.sys_save &&
    first.sys_export === second.sys_export &&
    first.sys_export_layout === second.sys_export_layout &&
    first.created_at === second.created_at
  );
}

function areStatusShallowEqual(
  first?: ItemStatus,
  second?: ItemStatus,
): boolean {
  if (!first && !second) return true;
  if (!first || !second) return false;

  return (
    first.oit_status_id === second.oit_status_id &&
    first.descricao === second.descricao
  );
}

function areProdutosShallowEqual(
  first?: Produto,
  second?: Produto,
): boolean {
  if (!first && !second) return true;
  if (!first || !second) return false;

  return (
    first.pro_id === second.pro_id &&
    first.nome === second.nome &&
    first.tipo === second.tipo &&
    first.categoria === second.categoria &&
    first.quantidade === second.quantidade &&
    first.unidade?.umd_id === second.unidade?.umd_id &&
    first.unidade?.descricao === second.unidade?.descricao &&
    first.unidade?.sigla === second.unidade?.sigla
  );
}

function areItensShallowEqual(first: Item, second: Item): boolean {
  return (
    first.oit_id === second.oit_id &&
    first.quantidade === second.quantidade &&
    first.observacao === second.observacao &&
    first.valor_unitario === second.valor_unitario &&
    first.valor_total === second.valor_total &&
    first.open_only === second.open_only &&
    JSON.stringify(first.linked_context || null) ===
      JSON.stringify(second.linked_context || null) &&
    first.is_impressao_vinculada === second.is_impressao_vinculada &&
    first.is_consumo_base === second.is_consumo_base &&
    JSON.stringify(first.produto_personalizado_consumo || null) ===
      JSON.stringify(second.produto_personalizado_consumo || null) &&
    areStatusShallowEqual(first.status, second.status) &&
    areArquivosShallowEqual(first.arquivo, second.arquivo) &&
    areProdutosShallowEqual(first.produto, second.produto)
  );
}

function areClientesShallowEqual(
  first?: Cliente,
  second?: Cliente,
): boolean {
  if (!first && !second) return true;
  if (!first || !second) return false;

  return (
    first.cli_id === second.cli_id &&
    first.nome === second.nome &&
    first.razao_social === second.razao_social &&
    first.nome_fantasia === second.nome_fantasia
  );
}

function areStatusPedidoEqual(
  first?: PedidoStatus,
  second?: PedidoStatus,
): boolean {
  if (!first && !second) return true;
  if (!first || !second) return false;

  return (
    first.id_pagamento === second.id_pagamento &&
    first.descricao === second.descricao &&
    first.descricao_pagamento === second.descricao_pagamento
  );
}

function mergeArquivo(
  previous?: ArquivoInfo | null,
  next?: ArquivoInfo | null,
): ArquivoInfo | undefined {
  if (!previous && !next) return undefined;

  const previousArquivo: Partial<ArquivoInfo> = previous ?? {};
  const nextArquivo: Partial<ArquivoInfo> = next ?? {};

  return {
    id: nextArquivo.id ?? previousArquivo.id ?? 0,
    nome:
      nextArquivo.nome ??
      nextArquivo.nome_arq ??
      previousArquivo.nome ??
      previousArquivo.nome_arq ??
      null,
    diretorio:
      nextArquivo.diretorio ?? previousArquivo.diretorio ?? null,
    sys_save: nextArquivo.sys_save ?? previousArquivo.sys_save ?? false,
    sys_export:
      nextArquivo.sys_export ?? previousArquivo.sys_export ?? false,
    sys_export_layout:
      nextArquivo.sys_export_layout ??
      previousArquivo.sys_export_layout ??
      false,
    sys_update:
      nextArquivo.sys_update ?? previousArquivo.sys_update ?? null,
    created_at:
      nextArquivo.created_at ?? previousArquivo.created_at ?? null,
  };
}

function mergeProgresso(
  previous?: Record<string, unknown>,
  next?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!previous && !next) return undefined;
  return { ...(previous ?? {}), ...(next ?? {}) };
}

function mergeItem(previous: Item, next: Item): Item {
  const merged: Item = { ...previous, ...next };

  if (next.quantidade !== undefined && next.quantidade !== null) {
    merged.quantidade = next.quantidade;
  }
  if (next.valor_total !== undefined && next.valor_total !== null) {
    merged.valor_total = next.valor_total;
  }
  if (next.valor_unitario !== undefined && next.valor_unitario !== null) {
    merged.valor_unitario = next.valor_unitario;
  }
  if (next.observacao !== undefined && next.observacao !== null) {
    merged.observacao = next.observacao;
  }
  if (next.open_only !== undefined) {
    merged.open_only = next.open_only;
  }
  if (next.linked_context !== undefined) {
    merged.linked_context = next.linked_context;
  }
  if (next.is_impressao_vinculada !== undefined) {
    merged.is_impressao_vinculada = next.is_impressao_vinculada;
  }
  if (next.is_consumo_base !== undefined) {
    merged.is_consumo_base = next.is_consumo_base;
  }
  if (next.produto_personalizado_consumo !== undefined) {
    merged.produto_personalizado_consumo =
      next.produto_personalizado_consumo;
  }

  merged.arquivo = mergeArquivo(previous.arquivo, next.arquivo);
  merged.progresso = mergeProgresso(previous.progresso, next.progresso);

  if (!next.regras && previous.regras) {
    merged.regras = previous.regras;
  }

  return merged;
}

function isPedidoTopLevelEqual(first: Pedido, second: Pedido): boolean {
  return (
    first.orc_id === second.orc_id &&
    first.observacao === second.observacao &&
    first.nome_arquivo === second.nome_arquivo &&
    first.valor_total === second.valor_total &&
    first.data_entrega === second.data_entrega &&
    first.data_cadastro === second.data_cadastro &&
    first.data_emissao === second.data_emissao &&
    first.valor_frete === second.valor_frete &&
    first.forma_pagamento_id === second.forma_pagamento_id &&
    first.valor_desconto === second.valor_desconto &&
    areClientesShallowEqual(first.cliente, second.cliente) &&
    areStatusPedidoEqual(first.status, second.status)
  );
}

export function mergePedidosIncremental(
  novosPedidos: readonly Pedido[],
  pedidosExistentes: readonly Pedido[],
): Pedido[] {
  const existentesMap = new Map<string, Pedido>();
  for (const pedido of pedidosExistentes) {
    existentesMap.set(pedido.orc_id.toString(), pedido);
  }

  const result = novosPedidos.map((novoPedido) => {
    const pedidoExistente = existentesMap.get(novoPedido.orc_id.toString());
    if (!pedidoExistente) {
      return novoPedido;
    }

    const itensMap = new Map(
      pedidoExistente.itens.map((item) => [item.oit_id, item]),
    );
    const itensMesclados = novoPedido.itens.map((novoItem) => {
      const itemExistente = itensMap.get(novoItem.oit_id);
      if (!itemExistente) {
        return novoItem;
      }

      const merged = mergeItem(itemExistente, novoItem);
      return areItensShallowEqual(merged, itemExistente)
        ? itemExistente
        : merged;
    });
    const itensIguais =
      itensMesclados.length === pedidoExistente.itens.length &&
      itensMesclados.every(
        (item, index) => item === pedidoExistente.itens[index],
      );

    if (isPedidoTopLevelEqual(novoPedido, pedidoExistente) && itensIguais) {
      return pedidoExistente;
    }

    return { ...novoPedido, itens: itensMesclados };
  });

  return result.sort((first, second) => second.orc_id - first.orc_id);
}
